import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Flame, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Wind, UtensilsCrossed } from 'lucide-react';

const CommercialPizzaOvenCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "What does a pizza oven clean actually involve?",
      a: "A full strip-down, removing racks, conveyors and internal components to clear baked-on grease and carbon build-up, not just a surface wipe."
    },
    {
      q: "Can you clean the canopy and exhaust above the oven at the same time?",
      a: "Yes, we often combine pizza oven cleaning with canopy, duct and rooftop fan cleaning in the same visit, covering the oven and the exhaust system above it together."
    },
    {
      q: "How many ovens can you clean in one visit?",
      a: "Up to three ovens in a single night, depending on the site."
    },
    {
      q: "Which pizza brands do you work with?",
      a: "We work with Domino's, Romano's Pizzeria, Pizza Hut and Crust Pizza across WA, along with boutique pizzerias and larger sites like Costco."
    },
    {
      q: "Do you clean large-format ovens like those used at Costco food courts?",
      a: "Yes, we clean Costco's large XLT ovens, rotisseries and exhaust system, alongside standard commercial pizza ovens."
    },
    {
      q: "How often are pizza ovens cleaned?",
      a: "It depends on the site and usage. Domino's and Crust locations are typically cleaned on a 4 to 6-monthly schedule, and we can recommend a frequency based on your kitchen's volume."
    }
  ];

  const relatedServices = [
    {
      title: 'Canopy, Duct & Rooftop Fan Cleaning',
      description: 'AS 1851:2012 compliant exhaust cleaning above the oven.',
      link: '/services/canopy-duct-fan-cleaning-perth',
      icon: Wind
    },
    {
      title: 'Commercial Kitchen Cleaning',
      description: 'Full kitchen cleaning alongside oven and exhaust work.',
      link: '/services/commercial-kitchen-cleaning-perth',
      icon: UtensilsCrossed
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial Pizza Oven Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "Full strip-down pizza oven cleaning for Domino's, Romano's Pizzeria, Pizza Hut, Crust Pizza, Costco and boutique pizzerias. Up to 3 ovens per night."
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/commercial-pizza-oven-cleaning-perth" />
        <title>Commercial Pizza Oven Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Full strip-down pizza oven cleaning across Perth and WA. Domino's, Pizza Hut, Crust Pizza, Romano's, Costco. Up to 3 ovens per night. The job that started it all in 1999." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="commercial-pizza-oven-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1588416820614-f8d6ac6cea56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGNoZWYlMjBjb29raW5nJTIwZ3JpbGwlMjByZXN0YXVyYW50fGVufDB8fHx8MTc4OTM1NTI2MHww&ixlib=rb-4.1.0&q=85"
              alt="Commercial kitchen with stainless steel equipment"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Flame size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Pizza Oven Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Commercial Pizza Oven Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Full strip-down pizza oven cleaning, the job that started it all in 1999
              </p>
              <p className="text-gray-400 mb-8">
                The business started with one Domino's pizza oven a night in 1999, fully stripped down and high-pressure cleaned from the back of a truck, before becoming Extreme Clean Australia in 2006. That same hands-on approach has scaled up ever since, we now clean up to three ovens in a single night. We work with Domino's, Romano's Pizzeria, Pizza Hut, Crust Pizza, boutique pizzerias across Perth, and Costco, cleaning their large XLT ovens, rotisseries and exhaust system. We often combine oven cleaning with canopy, duct and rooftop fan cleaning, plus rotary floor and extraction cleaning for a complete pizza kitchen visit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                A full strip-down clean of commercial pizza ovens, removing racks, conveyors and internal components to clear baked-on grease and carbon build-up that a surface wipe-down can't touch. Where needed, we combine this with canopy, duct and rooftop fan cleaning above the oven, and rotary floor and extraction cleaning underneath and around it, covering the full space a pizza oven affects, not just the oven itself.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Full strip-down oven cleaning',
                  'Rack & conveyor removal',
                  'Grease & carbon build-up removal',
                  'Canopy & exhaust cleaning',
                  'Rotary floor cleaning',
                  'Up to 3 ovens per night'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* From One Oven to Three */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">History</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">From One Oven a Night to Three</h2>
              <p className="text-gray-600 mb-8">
                This is the service that built the business. In 1999, cleaning a single Domino's oven properly took a full night's work. Since then, the process has scaled without losing the standard, we now complete up to three ovens in a single night, working with Domino's, Romano's Pizzeria, Pizza Hut and Crust Pizza across WA, along with boutique pizzerias and larger operations like Costco, where we clean their three XLT ovens, rotisseries and exhaust system. Domino's and Crust are typically cleaned on a 4 to 6-monthly schedule, depending on the site.
              </p>
              <div className="flex flex-wrap gap-3">
                {["Domino's", "Romano's Pizzeria", 'Pizza Hut', 'Crust Pizza', 'Costco', 'Boutique Pizzerias'].map((brand, index) => (
                  <span key={index} className="bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                    {brand}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Complete Pizza Kitchen Visit */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Complete Service</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">A Complete Pizza Kitchen Visit</h2>
              <p className="text-gray-600">
                Pizza kitchens have their own particular build-up, grease and carbon inside the oven, and grease in the canopy and exhaust system above it. Rather than treating these as separate jobs, we often combine pizza oven cleaning with canopy, duct and rooftop fan cleaning, and rotary floor and extraction cleaning, one visit covering the oven and everything around it.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Why Us</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Why Businesses Choose Extreme Clean Australia</h2>
              <p className="text-gray-300">
                This is where the business started, and it shows. Since 1999, full strip-down pizza oven cleaning has meant we know exactly what builds up, where, and how to remove it properly, not just wipe the surface. That's the same standard whether it's one oven or three in a night.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Pizza Oven Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for full strip-down pizza oven cleaning, from single ovens to multi-oven sites.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommercialPizzaOvenCleaning;
